import { ROUTES } from "routes/routes";
import useDomain from "hooks/useDomain";
import useModals from "hooks/useModals";
import { useEffect, useState } from "react";
import { ISendQuestion } from "../interfaces";
import { useConfigPromp } from "routes/context";
import useResponsive from "hooks/useResponsive";
import useGetCodeError from "hooks/useGetCodeError";
import useLocalStorage from "hooks/useLocalStorage";
import useNetworkStatus from "hooks/useNetworkStatus";
import { ERROR_NOT_IN_PROGRESS, ERROR_READY_ANSWERED, ERROR_REMAINING_ZERO } from "utils/ConstError";
import { useNavigate, useParams } from "react-router-dom";
import { initialStateAdmission, initialStateSendQuestion } from "../utils";
import { StatusesSend, TStatusSend } from "utils/InterfaceAndTypeGeneral";
import useInteractionTracker from "hooks/useInteractionTracker";
import { FROM_LOAD_QUIZZES, FROM_RESPONSE_EXAM, FROM_START_EXAMEN } from "utils/ConstantsAndNum";

const useEvaluation = () => {
  const isOnline = useNetworkStatus();
  const { isMinorTablet } = useResponsive();
  const getCodeError = useGetCodeError();
  const [errorServer, setValue, removeStorage] = useLocalStorage(
    "error_exam",
    0
  );
  const {
    isOpenModal,
    openModal,
    closeModal,
    closeAllModals,
    registerModal,
    unregisterModal,
    getConfigModal,
  } = useModals();
  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true);
  const { setBlocking, setHeader, isBlocking } = useConfigPromp();
  const {markInteractionStart,markInteractionEnd} = useInteractionTracker();
  const navigate = useNavigate();
  const { idAdmission, idQuizzes } = useParams<{
    idAdmission: string;
    idQuizzes: string;
  }>();
  const [isErrorAnswered, setIsErrorAnswered] = useState<boolean>(false);
  const [isErrorRemaining, setIsErrorRemaining] = useState<boolean>(false);
  const [isErrorSend, setIsErrorSend] = useState<boolean>(false);
  const [isErrorNotInProgress, setIsErrorNotInProgress] = useState<boolean>(false);
  const [isInactiveTimer, setIsInactiveTimer] = useState<boolean>(true);
  const [sendQuestion, setSendQuestion] = useState<ISendQuestion>(
    initialStateSendQuestion
  );

  const {
    run: runQuizzes,
    data: dataQuizzes,
    isError: isErrorQuizzes,
    isLoading: isLoadingQuizzes,
  } = useDomain({
    initState: initialStateAdmission,
  });

  const {
    run: runSend,
    isLoading: isLoadingSend,
    isError: isErrorSendDomain,
  } = useDomain();

  const handleGetQuizzes = async (typeTracker: string,messageTracker: string) => {
    if(FROM_RESPONSE_EXAM !== typeTracker && FROM_START_EXAMEN !== typeTracker) {
      markInteractionStart(typeTracker, messageTracker);
    }
    const { data, error } = await runQuizzes((domain: any) =>
      domain.get_quizzes_use_case.execute(idAdmission, idQuizzes)
    );
    markInteractionEnd(typeTracker, messageTracker);
    if (error) {
      if (getCodeError(error) === ERROR_NOT_IN_PROGRESS) {
        setIsErrorNotInProgress(
          prev => !prev
        );
        return;
      }
      if (getCodeError(error) === ERROR_REMAINING_ZERO) {
        openModal("timeFinish");
        setIsErrorRemaining(true);
        setIsLoadingFirst(false);
        return;
      }
      closeAllModals();
      setValue(errorServer + 1);
    }

    setSendQuestion((prev) => ({
      ...prev,
      answerId: "",
      questionId: data?.question?.id,
    }));
    setIsLoadingFirst(false);
  };

  const handleOptionOption = (option: string) => {
    setSendQuestion((prev) => ({
      ...prev,
      answerId: option,
    }));
  };

  const handleSendQuestion = async (
    senBy: TStatusSend,
    isGetQuizz: boolean
  ) => {
    closeModal("notResponse");
    const send = {
      ...sendQuestion,
      sentBy: senBy,
    };
    markInteractionStart(FROM_RESPONSE_EXAM, dataQuizzes?.question?.isLastQuestion ? "Finalizar examen" : "Responder pregunta");
    const { error } = await runSend((domain: any) =>
      domain.post_send_questions_use_case.execute(idAdmission, idQuizzes, send)
    );

    if (error) {
      markInteractionEnd(FROM_RESPONSE_EXAM, "Responder pregunta");
      if (getCodeError(error) === ERROR_NOT_IN_PROGRESS) {
        return setIsErrorNotInProgress(true);
      }
      if (getCodeError(error) === ERROR_READY_ANSWERED) {
        setIsErrorAnswered(true);
        openModal("alreadyResponse");
        return;
      }
      setIsErrorSend(true);
      return setValue(errorServer + 1);
    }
    if(senBy === StatusesSend.CANDIDATE ) {
      if (!dataQuizzes?.question?.isLastQuestion && isGetQuizz) {
        return handleGetQuizzes(FROM_RESPONSE_EXAM,'Responder pregunta');
      } else {
        openModal("finishExam");
        return markInteractionEnd(FROM_RESPONSE_EXAM, "Finalizar examen");
      }
    }
    markInteractionEnd(FROM_RESPONSE_EXAM, "Responder pregunta");
  };

  const handleNextQuestion = () => {
    if (!isOnline) return openModal("internet");
    if (sendQuestion.answerId === "") return openModal("notResponse");
    handleSendQuestion(StatusesSend.CANDIDATE, true);
  };

  const handleOpenTimeFinishModal = async () => {
    if (isErrorQuizzes) return;
    setIsInactiveTimer(false);
    await handleSendQuestion(StatusesSend.TIMER, false);
    handleCloseInactiveModal();
    if(!isErrorSendDomain){
      openModal("timeFinish");
    }
  };

  const handleCloseFinishExam = () => {
    closeAllModals();
    removeStorage();
    navigate(ROUTES.candidate.delivery);
  };

  const handleCloseTimeFinishModal = () => {
    closeModal("timeFinish");
    navigate(ROUTES.candidate.delivery);
  };

  const handleOpenInactiveModal = () => {
    openModal("inactive");
  };

  const handleCloseInactiveModal = () => {
    closeModal("inactive");
  };

  const handleOpenTwoMinutesModal = () => {
    openModal("twoMinutes");
  };

  const handleCloseTwoMinutesModal = () => {
    closeModal("twoMinutes");
  };

  useEffect(() => {
    registerModal("notResponse", {
      onConfirm: () => handleSendQuestion(StatusesSend.CANDIDATE, true),
      onCancel: () => closeModal("notResponse"),
    });

    return () => {
      unregisterModal("notResponse");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerModal, unregisterModal, sendQuestion?.questionId]);

  useEffect(() => {
    if (isErrorAnswered) {
      closeModal("inactive");
      closeModal("timeFinish");
      closeModal("twoMinutes");
      closeModal("internet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorAnswered]);

  useEffect(() => {
    registerModal("inactive");
    registerModal("internet");
    registerModal("timeFinish");
    registerModal("twoMinutes");
    registerModal("finishExam");
    registerModal("alreadyResponse");
    return () => {
      unregisterModal("inactive");
      unregisterModal("internet");
      unregisterModal("timeFinish");
      unregisterModal("twoMinutes");
      unregisterModal("finishExam");
      unregisterModal("alreadyResponse");
    };
  }, [registerModal, unregisterModal]);

  useEffect(() => {
    const isBlocking = !isOpenModal("timeFinish") && !isOpenModal("finishExam") && !isErrorNotInProgress;
    setHeader(!isMinorTablet);
    setBlocking(isBlocking);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal("timeFinish"), isOpenModal("finishExam"), isMinorTablet,isErrorNotInProgress]);

  useEffect(() => {

    if (!isBlocking && isErrorNotInProgress) {
      navigate(ROUTES.candidate.delivery);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocking, isErrorNotInProgress]); 

  useEffect(() => {
    if (isErrorSendDomain && !isErrorAnswered) {
      closeAllModals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorSendDomain]);

  useEffect(() => {
    const existSessionStart = sessionStorage.getItem(FROM_START_EXAMEN);
    const tagFromStart = existSessionStart ? FROM_START_EXAMEN : FROM_LOAD_QUIZZES;
    const tagFromName = existSessionStart ? 'Iniciar examen' : 'Vista examen';
    handleGetQuizzes(tagFromStart, tagFromName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOpenModal,
    sendQuestion,
    isMinorTablet,
    getConfigModal,
    isErrorRemaining,
    isInactiveTimer,
    handleNextQuestion,
    handleOptionOption,
    handleGetQuizzes,
    handleCloseFinishExam,
    handleOpenInactiveModal,
    handleCloseInactiveModal,
    handleOpenTwoMinutesModal,
    handleOpenTimeFinishModal,
    handleCloseTimeFinishModal,
    handleCloseTwoMinutesModal,
    isManyError: errorServer > 1,
    titleValue: dataQuizzes?.title, 
    question: dataQuizzes?.question,
    remainingTime: dataQuizzes?.remainingTime,
    allQuestions: dataQuizzes?.totalQuestions,
    isError: (isErrorQuizzes && !isErrorRemaining) || (isErrorSend && !isErrorAnswered),
    isLoadingFirst,
    isLoading: isLoadingQuizzes || isLoadingSend,
  };
};

export default useEvaluation;
